import React, { useMemo, useState } from "react"
import { formatMoney, percentageLabel } from "./formatters"
import { QualityBadge } from "./DashboardComponents"

const number = value => value === null || value === undefined
  ? "Unavailable"
  : Number(value).toLocaleString("en-GB", { maximumFractionDigits: 2 })

const comparison = metric => metric
  ? `${number(metric.current)} / ${number(metric.comparison)}`
  : "Hidden for this lens"
const percentage = value => value === null || value === undefined ? "Unavailable" : `${number(value)}%`
const percentageComparison = metric => metric
  ? `${percentage(metric.current)} / ${percentage(metric.comparison)}`
  : "Unavailable"

const RegionsClubsView = ({ snapshot, onLensChange }) => {
  const view = snapshot.regionsClubs
  const [sort, setSort] = useState({ key: "participation", direction: "desc" })
  const home = view.lens === "player-home"
  const rows = useMemo(() => [...view.rows].sort((left, right) => {
    const leftValue = sort.key === "region" ? left.label : left[sort.key]?.current
    const rightValue = sort.key === "region" ? right.label : right[sort.key]?.current
    const result = typeof leftValue === "string"
      ? leftValue.localeCompare(rightValue)
      : Number(leftValue || 0) - Number(rightValue || 0)
    return sort.direction === "asc" ? result : -result
  }), [sort, view.rows])
  const changeSort = key => setSort(current => ({
    key,
    direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
  }))
  const active = view.activeClubs?.breakdown

  return (
    <section className="analytics-regions-view" aria-labelledby="regions-title">
      <header className="analytics-view-heading">
        <div>
          <p className="analytics-eyebrow">Regions & Clubs</p>
          <h2 id="regions-title">Regional demand & active supply</h2>
          <p>Operational Region reuses the newsletter taxonomy ({view.mappingVersion}) and preserves Unmapped.</p>
        </div>
        <div className="analytics-region-lenses" aria-label="Regional analysis lens">
          <button type="button" aria-pressed={!home} onClick={() => onLensChange("activity-location")}>Activity Location</button>
          <button type="button" aria-pressed={home} onClick={() => onLensChange("player-home")}>Player Home</button>
        </div>
      </header>

      {home ? (
        <aside className="analytics-definition-note analytics-region-lens-note">
          <strong>Player Home is a local demand lens.</strong>
          <p>It shows where players originate; venue revenue and club supply are hidden and never reassigned.</p>
        </aside>
      ) : active && (
        <section className="analytics-club-health" aria-label="Active Club channels">
          <article><span>Event-only clubs</span><strong>{number(active.current.eventOnly)}</strong><small>vs {number(active.comparison.eventOnly)}</small></article>
          <article><span>Booking-only clubs</span><strong>{number(active.current.bookingOnly)}</strong><small>vs {number(active.comparison.bookingOnly)}</small></article>
          <article><span>Active through both</span><strong>{number(active.current.both)}</strong><small>vs {number(active.comparison.both)}</small></article>
          <article><span>Total Active Clubs</span><strong>{number(active.current.total)}</strong><small>vs {number(active.comparison.total)} · {percentageLabel(view.activeClubs.delta.percentage)}</small></article>
        </section>
      )}

      {!home && (
        <section className="analytics-region-growth" aria-labelledby="region-growth-title">
          <div className="analytics-section-heading">
            <div><h3 id="region-growth-title">Demand Growth vs Supply Growth</h3><p>Bubble size represents Confirmed Participation. No rigid oversupply/undersupply labels are applied.</p></div>
          </div>
          <div className="analytics-region-bubbles" role="img" aria-label="Participation growth versus eligible event-count growth by Operational Region">
            {rows.filter(row => row.participation.deltaPercentage !== null && row.eligibleEvents?.deltaPercentage !== null).map(row => {
              const x = Math.max(8, Math.min(92, 50 + row.eligibleEvents.deltaPercentage * 3))
              const y = Math.max(10, Math.min(90, 50 - row.participation.deltaPercentage * 2))
              const size = Math.max(34, Math.min(72, 28 + Math.sqrt(row.participation.current)))
              return <span key={row.key} style={{ left: `${x}%`, top: `${y}%`, width: size, height: size }} title={`${row.label}: demand ${percentageLabel(row.participation.deltaPercentage)}, supply ${percentageLabel(row.eligibleEvents.deltaPercentage)}`}>{row.label}</span>
            })}
            <i className="analytics-region-x-axis">Eligible Event-count growth →</i>
            <i className="analytics-region-y-axis">Participation growth →</i>
          </div>
        </section>
      )}

      <section className="analytics-region-table-section" aria-labelledby="region-table-title">
        <div className="analytics-section-heading">
          <div><h3 id="region-table-title">Operational Region comparison</h3><p>Current / Comparison values use the exact global periods above.</p></div>
        </div>
        <div className="analytics-table-wrap">
          <table className="analytics-data-table analytics-region-table">
            <thead><tr>
              <th><button type="button" onClick={() => changeSort("region")}>Region</button></th>
              <th><button type="button" onClick={() => changeSort("participation")}>Participation</button></th>
              <th>Demand change</th>
              {!home && <th>Eligible Events</th>}
              {!home && <th>Participation / Event</th>}
              {!home && <th>Active Clubs</th>}
              {!home && <th>Venue Net Revenue</th>}
              <th>Event Participants</th>
              <th>Frequency</th>
              <th>Event churn</th>
              {!home && <th>Withdrawal after close</th>}
            </tr></thead>
            <tbody>{rows.map(row => <tr key={row.key}>
              <th scope="row">{row.label}</th>
              <td>{comparison(row.participation)}</td>
              <td>{percentageLabel(row.participation.deltaPercentage)}</td>
              {!home && <td>{comparison(row.eligibleEvents)}</td>}
              {!home && <td>{comparison(row.participationPerEvent)}</td>}
              {!home && <td>{comparison(row.activeClubs)}</td>}
              {!home && <td>{row.venueNetRevenue ? `${formatMoney(row.venueNetRevenue.current)} / ${formatMoney(row.venueNetRevenue.comparison)}` : "Unavailable"}</td>}
              <td>{comparison(row.eventParticipants)}</td>
              <td>{comparison(row.frequency)}</td>
              <td>{percentageComparison(row.eventChurn)}</td>
              {!home && <td>{percentageComparison(row.withdrawalRate)}</td>}
            </tr>)}</tbody>
          </table>
        </div>
        <aside className="analytics-definition-note analytics-region-reconciliation-note">
          <strong>Regional unique players may not be summed across regions. <QualityBadge quality={view.quality?.current} /></strong>
          <p>{view.quality?.current?.reason || "Regional reconciliation evidence is unavailable."} Unmapped is a population, not a quality state.</p>
        </aside>
      </section>

      {!home && view.clubs?.length > 0 && (
        <section className="analytics-region-table-section analytics-club-detail" aria-labelledby="club-table-title">
          <div className="analytics-section-heading">
            <div>
              <h3 id="club-table-title">Active Club detail</h3>
              <p>Ranked by current event participation, then by event and elapsed-booking activity.</p>
            </div>
          </div>
          <div className="analytics-table-wrap">
            <table className="analytics-data-table" aria-label="Active Club current and comparison detail">
              <thead><tr>
                <th>Club</th>
                <th>Operational Region</th>
                <th>Activity channel</th>
                <th>Participation</th>
                <th>Eligible Events</th>
                <th>Elapsed Bookings</th>
              </tr></thead>
              <tbody>{view.clubs.map(club => <tr key={club.clubId}>
                <th scope="row" className="analytics-club-name">
                  <strong>{club.label}</strong>
                  <small>{club.city || "City unavailable"}</small>
                </th>
                <td>{club.region}</td>
                <td>{club.activity.current} / {club.activity.comparison}</td>
                <td>{comparison(club.participation)}</td>
                <td>{comparison(club.events)}</td>
                <td>{comparison(club.bookings)}</td>
              </tr>)}</tbody>
            </table>
          </div>
        </section>
      )}
    </section>
  )
}

export default RegionsClubsView
